import { Component, computed, effect, input, OnDestroy, Signal, signal, TemplateRef } from '@angular/core';
import { ActiveToast, ToastAction, ToastType } from '@app/portal/services/toast-service';
import { SharedModule } from '@app/shared/shared.module';
import { Translatable } from '@app/translate/models/translatable';

const IconClasses: Record<ToastType, string> = {
  danger: 'far fa-bolt',
  default: '',
  info: 'far fa-info-circle',
  success: 'far fa-check',
  warning: 'far fa-exclamation-triangle',
};

const ToastClasses: Record<ToastType, string> = {
  danger: 'portal-toast--danger',
  default: 'portal-toast--default',
  info: 'portal-toast--info',
  success: 'portal-toast--success',
  warning: 'portal-toast--warning',
};

@Component({
  selector: 'portal-toast',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './toast.component.html',
  styles: ``,
  host: {
    '(click)': 'onClick()',
    '(mouseenter)': 'autoHide.stop()',
    '(mouseleave)': 'autoHide.start()',
    '[class]': 'cssClasses()',
    '[class.portal-toast--progress-enabled]': 'toast().showProgress != null',
    '[class.show]': 'autoHide.visible()',
    'aria-atomic': 'true',
    'aria-live': 'assertive',
    role: 'alert',
  },
})
export class ToastComponent implements OnDestroy {
  toast = input.required<ActiveToast>();
  cssClasses = computed(() => [ToastClasses[this.toast().type], 'toast portal-toast'].join(' '));
  iconClasses = computed(() => IconClasses[this.toast().type]);
  autoHide = progressHandler(this.toast);

  ngOnDestroy(): void {
    this.autoHide.stop();
  }

  hide(): void {
    this.toast().hide();
  }

  invokeAction(action: ToastAction): void {
    action.invoke({
      hide: () => this.toast().hide(),
    });
  }

  isTemplate(value: string | Translatable | TemplateRef<any>): value is TemplateRef<any> {
    return value instanceof TemplateRef;
  }

  onClick(): void {
    if (this.toast().header == null) {
      this.hide();
    }
  }
}

function progressHandler(toast: Signal<ActiveToast>) {
  const progress = signal(0);
  const increasePerDraw = signal(0);
  const visible = signal(false);

  effect(update);

  let timeoutId: number | null = null;

  return {
    progress,
    visible,
    start,
    stop,
  };

  function run(): void {
    progress.update((progress) => progress + increasePerDraw());

    if (progress() < 100) {
      timeoutId = window.setTimeout(run, 16);
    } else {
      timeoutId = null;
      toast().hide();
    }
  }

  function start(): void {
    if (timeoutId == null) {
      if (toast().showProgress) {
        timeoutId = window.setTimeout(run, 16);
      } else if (toast().delay > 0) {
        timeoutId = window.setTimeout(() => toast().hide(), toast().delay);
      }
    }
  }

  function stop(): void {
    if (timeoutId != null) {
      visible.set(false);
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  }

  function update(): void {
    if (toast() != null) {
      visible.set(true);

      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }

      if (toast().showProgress) {
        const toastVisibleForMs = toast().delay - 400;
        const percentPerSecond = (1 / (toastVisibleForMs / 100)) * 1000;
        const drawsPerSecond = 1000 / 16;

        increasePerDraw.set(percentPerSecond / drawsPerSecond);
        start();
      } else if (toast().delay > 0) {
        timeoutId = window.setTimeout(() => toast().hide(), toast().delay);
      }
    } else {
      stop();
    }
  }
}
